import { NumberInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import type { CalculationFormSchema } from "@msuite/utils/schemas";
import { calculateMeasurementForUnit } from "@msuite/utils/services";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { CalculationFormItemPositionFieldsSharedProps } from "./calculation-form-item-position-fields-shared";
import { CalculationFormItemPositionFieldsMeasurable } from "./calculation-form-item-position-fields-measurable";
import { CalculationFormItemPositionFieldsPiece } from "./calculation-form-item-position-fields-piece";
import { CalculationFormItemPositionFieldsResult } from "./calculation-form-item-position-fields-result";

export function CalculationFormItemPositionFieldsCubicMeters({
	index,
	positionIndex,
}: CalculationFormItemPositionFieldsSharedProps) {
	/** Context */
	const methods = useFormContext<CalculationFormSchema>();
	const [quantity, length, width, height, manualMeasurement] = useWatch({
		control: methods.control,
		name: [
			`services.${index}.positions.${positionIndex}.quantity`,
			`services.${index}.positions.${positionIndex}.length`,
			`services.${index}.positions.${positionIndex}.width`,
			`services.${index}.positions.${positionIndex}.height`,
			`services.${index}.positions.${positionIndex}.manualMeasurement`,
		],
	});

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const result = calculateMeasurementForUnit("cubic-meters", {
		quantity,
		length,
		width,
		height,
		manualMeasurement,
	});

	/** Render */
	return (
		<React.Fragment>
			<CalculationFormItemPositionFieldsPiece
				index={index}
				positionIndex={positionIndex}
				step={0.5}
			/>
			<CalculationFormItemPositionFieldsMeasurable
				index={index}
				positionIndex={positionIndex}
				measurementKeys={["length", "width", "height"]}
				variant="cubic-meters"
			>
				<NumberInput
					isRequired
					label={t("calculations.length")}
					control={methods.control}
					name={`services.${index}.positions.${positionIndex}.length`}
					min={0}
					step={0.01}
					variant="meters"
					errorVariant="tooltip"
				/>
				<NumberInput
					isRequired
					label={t("calculations.width")}
					control={methods.control}
					name={`services.${index}.positions.${positionIndex}.width`}
					min={0}
					step={0.01}
					variant="meters"
					errorVariant="tooltip"
				/>
				<NumberInput
					isRequired
					label={t("calculations.height")}
					control={methods.control}
					name={`services.${index}.positions.${positionIndex}.height`}
					min={0}
					step={0.01}
					variant="meters"
					errorVariant="tooltip"
				/>
			</CalculationFormItemPositionFieldsMeasurable>
			<CalculationFormItemPositionFieldsResult
				value={result}
				variant="cubic-meters"
			/>
		</React.Fragment>
	);
}
